import { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import isEqual from 'lodash.isequal';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import { notification } from 'antd';
import { v4 } from 'uuid';
import CreateEvaluationModalImage from '../assets/createEvaluation/CreateEvaluationModalImage';
import { t } from '../i18n/i18n';
import Modal from '../components/Modal/Modal';
import processQueryString from '../utils/processQueryString';
import { CREATE_EVALUATION, SCREENING_SEARCHES } from '../constants/routes';
import { setEvaluationData } from '../redux/actions/evaluationActions';
import {
  editRecruitSessionDraft,
  getAudioSignedUrl,
  getAutoSelectTestgroups,
  getImageSignedUrl,
  getPdfSignedUrl,
  getRecommendedTestgroups,
  getRecruitSessionDraft,
  getScreeningTestGroups,
  getSearchTypes,
  openEvaluation,
  postRecruitSessionDraft,
  uploadFileToS3,
} from '../API/services/screeningService';
import useCreateScreening from './useCreateScreening';
import {
  setCustomTestGroupIncompleteQuestion,
  setEvaluationSearchTypes,
  setNewScreeningData,
  setNewScreeningFilters,
  setNewSearchData,
  setSearchInvitationsData,
  setSendEmailFrom,
  setTestGroupsData,
} from '../redux/actions/screeningActions';
import { formatMinutesToHours } from '../utils/formatMinutesToHours';
import { formatOrder } from '../utils/formatArrayOrder';
import { TrackEventGTM } from '../utils/filters/analyticsUtils';
import {
  AnswerTypeEnum,
  CATEGORY_TYPES,
  EVALUATION_SEARCH_TYPES,
  EvaluationPurposes,
  QuantitativeAnswerTypeArray,
  ReviewTypeEnum,
  TEMP_NEW_TEST_CREATION,
  TestFileType,
  StrategyValues,
  RecordingMode,
  TestGroupTypes,
} from '../constants/enums';
import { getLocalStorageWorkspace } from '../utils/workspaceUtils';
import { convertMinToMinutesNumber } from '../constants/hours';
import useTranslations from './useTranslations';
import { getTestGroupScreeningStrategy } from '../utils/testgroups';
import useCreateTestGroup from './useCreateTestGroup';
import { removeQueryParams } from '../utils/removeQueryParams';
import { EventsGTM } from '../constants/events';
import { getTranslatedValue } from '../utils/translationHelpers';

const useCreateEvaluation = () => {
  const {
    newScreening,
    customScreeningTotalTime,
    availableWeight,
    testsDuration,
    incompleteQuestionValidation,
    incompletePrefilterQuestionValidation,
    formatCustomTestGroupToPost,
    formatChallengeTestGroupToPost,
    handleClearFilters,
    formatFilters,
    handleSelectFilterOptionMultiple,
    deselectTg,
    testGroupRolesFilterOptions,
    customTestGroupStrategy,
    handleCustomTestGroupStrategy,
    recordingConfig,
    handleRecordingConfig,
  } = useCreateScreening();

  const [step, setStep] = useState(0);
  const [loadingCreate, setLoadingCreate] = useState(false);
  const [loadingTests, setLoadingTests] = useState(false);
  const [loadingCreateDraft, setLoadingCreateDraft] = useState(false);
  const [loadingCreateEvaluation, setLoadingCreateEvaluation] = useState(false);
  const [preventReload, setPreventReload] = useState(false);
  const { evaluation } = useSelector((state) => state.evaluation);
  const [previousData, setPreviousData] = useState(evaluation);
  const [previousWeightsAndOrders, SetPreviousWeightsAndOrders] = useState([]);
  const [showExitModal, setShowExitModal] = useState(false);
  const [enableCreateButton, setEnableCreateButton] = useState(false);
  const { user, currentWorkspace } = useSelector((state) => state.auth);
  const { newSearch, newScreeningFilters, searchTypes } = useSelector((state) => state.screenings);
  const dispatch = useDispatch();
  const history = useHistory();
  const { search } = useLocation();
  const queryData = processQueryString(search);
  const parsedCustomTGDuration = convertMinToMinutesNumber(newScreening?.customScreeningTGDuration);
  const { key } = useTranslations();
  const customTestGroup = newScreening?.allTests.find(
    (item) => item.type === TestGroupTypes.CUSTOM
  );

  const disableCustomTestGroupWeight =
    customTestGroup &&
    !customTestGroup?.tests?.some((test) => QuantitativeAnswerTypeArray.includes(test.answerType));

  const missingTestgroupWeight = false;

  const validCustomTestGroup = () => {
    const testsListLength = newScreening?.customTestGroup?.length;
    let validTestGroup = false;
    if (
      (testsListLength && parsedCustomTGDuration) ||
      (!testsListLength && !parsedCustomTGDuration)
    ) {
      validTestGroup = true;
    }

    return validTestGroup;
  };

  const uploadAndUpdateFile = async (file) => {
    if (!TestFileType[file.fileType]) return;
    let signedUrl;
    if (file.fileType === TestFileType.IMAGE)
      signedUrl = await getImageSignedUrl(file.fileObject.type);
    if (file.fileType === TestFileType.AUDIO)
      signedUrl = await getAudioSignedUrl(file.fileObject.type);
    if (file.fileType === TestFileType.PDF) signedUrl = await getPdfSignedUrl(file.fileObject.type);

    await uploadFileToS3(signedUrl.fileUrl, file.fileObject, file.fileObject.type);

    file.displayName = file.name;
    file.name = signedUrl.fileId;
    file.extension = signedUrl.fileExtension;
    file.url = removeQueryParams(signedUrl.fileUrl);
    delete file.fileObject;
  };

  const uploadAndUpdateFiles = async (tests) => {
    const testsPromises = tests?.flatMap(
      (test) =>
        test.testFiles
          ?.filter((file) => file.fileObject)
          .map((file) => uploadAndUpdateFile(file)) ?? []
    );

    const optionsPromises =
      tests.flatMap((test) =>
        test.options
          ?.filter((option) => option.files?.length)
          .flatMap((option) => option.files)
          .filter((file) => file.fileObject)
          .map((file) => uploadAndUpdateFile(file))
      ) ?? [];

    const promises = [...testsPromises, ...optionsPromises];

    await Promise.all(promises);
  };

  const validCustomChallengeTestGroup = () => {
    const testsListLength = newScreening?.customChallengeTestGroup?.length;
    let validTestGroup = false;
    if (testsListLength) {
      validTestGroup = true;
    }

    return validTestGroup;
  };

  const existingChanges = !isEqual(
    { ...previousData, weights: previousWeightsAndOrders },
    {
      ...evaluation,
      ...(newScreening?.testGroups?.length ? { testGroupList: newScreening?.testGroups } : {}),
      ...(newScreening?.prefilterQuestions
        ? { prefilterQuestions: newScreening.prefilterQuestions }
        : {}),
      ...(newScreening?.prefilterQuestionsDelay
        ? { prefilterQuestionsDelay: newScreening.prefilterQuestionsDelay }
        : {}),
      ...(newScreening?.customTestGroup?.length
        ? {
            customTestGroup: newScreening?.customTestGroup,
            customTestGroupHasSkipQuestion: newScreening?.customTestGroup?.hasSkipQuestion ?? true,
          }
        : {}),
      ...(newScreening?.customScreeningTGDuration?.length
        ? { customScreeningTGDuration: newScreening?.customScreeningTGDuration }
        : {}),
      weights: newScreening?.allTests?.map((item) => ({
        [item.id]: {
          weight: item.weight,
          order: item.order,
        },
      })),
      ...(newScreening.welcomeVideoUrl && newScreening.welcomeVideoTitle
        ? {
            welcomeVideoTitle: newScreening.welcomeVideoTitle,
            welcomeVideoUrl: newScreening.welcomeVideoUrl,
          }
        : { welcomeVideoTitle: null, welcomeVideoUrl: null }),
    }
  );

  const EvaluationPurposesOptions = [
    {
      id: EvaluationPurposes.TEST_THE_FEATURE,
      name: t('CREATE_EVALUATION_TEST_THE_FEATURE'),
    },
    {
      id: EvaluationPurposes.INTERN_PERSONAL_MAPPING,
      name: t('CREATE_EVALUATION_INTERN_PERSONAL_MAPPING'),
    },
    {
      id: EvaluationPurposes.EXTERNAL_PERSONAL_SEARCH,
      name: t('CREATE_EVALUATION_EXTERNAL_PERSONAL_SEARCH'),
    },
    {
      id: EvaluationPurposes.INTERNAL_PERSONAL_SEARCH,
      name: t('CREATE_EVALUATION_INTERNAL_PERSONAL_SEARCH'),
    },
    {
      id: EvaluationPurposes.MEASURE_SKILLS_FOR_TRAINING_PROGRAM,
      name: t('CREATE_EVALUATION_MEASURE_SKILLS_FOR_TRAINING_PROGRAM'),
    },
    {
      id: EvaluationPurposes.OTHER,
      name: t('CREATE_EVALUATION_OTHER'),
    },
  ];

  const disableEditButton = !existingChanges || !evaluation.name.length || !validCustomTestGroup();

  // Helper function to check test groups missing preset selection
  const getTestGroupsMissingPreset = (allTests) => {
    if (!allTests || !Array.isArray(allTests)) return [];

    return allTests.filter((testGroup) => {
      const hasAdvancedSettings =
        testGroup.advancedSetting?.available && testGroup.advancedSetting?.preSettings?.length > 1;

      if (!hasAdvancedSettings) return false;

      // Check if preset is truly missing (undefined or null), not just falsy
      const hasSelectedPreset =
        testGroup.selectedPresetId !== undefined && testGroup.selectedPresetId !== null;

      return !hasSelectedPreset;
    });
  };

  const hasTestGroupsWithoutPreset = getTestGroupsMissingPreset(newScreening?.allTests).length > 0;

  const disableOpenEvaluation =
    !evaluation?.name?.length ||
    !validCustomTestGroup() ||
    !newScreening.allTests.length ||
    availableWeight !== 0 ||
    missingTestgroupWeight ||
    hasTestGroupsWithoutPreset;

  const incorrectTestgroupLanguages = newScreening?.allTests
    ?.filter((item) => item.type !== TestGroupTypes.CUSTOM)
    ?.some(
      (tg) =>
        !(tg?.languages ?? ['es'])?.includes(evaluation.language) &&
        (!tg?.category?.type || tg?.category?.type !== CATEGORY_TYPES.LANGUAGE)
    );

  const resetDateHours = (date) => moment(date).set({ hour: 0, minute: 0 }).toISOString();
  const getDateHours = (date) => moment(date).format('HH:mm');

  const formatCustomTestGroupTests = (testGroupList) =>
    testGroupList?.map((item) => ({
      ...item,
      id: `${TEMP_NEW_TEST_CREATION}${v4()}`,
      deleteId: v4(),
      testImagesCount: 0,
      answerType: item.answerType,
    })) ?? [];

  const setFormattedData = (data) => {
    const formattedData = {
      name: data.name,
      startDate: {
        day: resetDateHours(data.startDate),
        hours:
          Number(getDateHours(data.startDate).split(':')[1]) % 15
            ? '00:00'
            : getDateHours(data.startDate),
      },
      endDate: {
        day: resetDateHours(data.endDate),
        hours: getDateHours(data.endDate),
      },
      language: data.language,
      testGroupList: data.testGroupList?.map((tg) => ({ ...tg, isSelected: true })) ?? [],
      customTestGroup: formatCustomTestGroupTests(data?.customTestGroup?.tests),
      customChallengeTestGroup: formatCustomTestGroupTests(data?.customChallengeTestGroup?.tests),
      customTestGroupHasSkipQuestion: data?.customTestGroup?.hasSkipQuestion ?? true,
      customScreeningTGDuration: formatMinutesToHours(data?.customTestGroup?.duration ?? 0, false),
      purpose: data.purpose,
      customPurpose: data.customPurpose,
      allowCertificateDownload: data.allowCertificateDownload ?? true,
      proctoringEnabled: data.proctoringEnabled ?? true,
      proctoringAIEnabled: data.proctoringAIEnabled ?? false,
      requireCompletedProfile: data.requireCompletedProfile ?? false,
      reminderToCandidates: data.reminderToCandidates ?? true,
      sectionsWithoutOrder: data.sectionsWithoutOrder ?? true,
      reviewType: data.reviewType ?? ReviewTypeEnum.GLOBAL,
      emptyDates: data.emptyDates ?? true,
      roleId: data.roleId,
      quickStartRoleId: data.quickStartRoleId,
      prefilterQuestions: data?.prefilterQuestions?.testGroups[0]?.tests,
      prefilterQuestionsDelay: data?.prefilterQuestions?.negativeDelay ?? undefined,
      prefilterQuestionDraftId: data?.prefilterQuestionDraftId,
      welcomeVideoUrl: data.welcomeVideoUrl,
      welcomeVideoTitle: data.welcomeVideoTitle,
      searchTypes: data.searchTypes,
    };
    dispatch(setEvaluationData(formattedData));
    const formattedAllTests = formatOrder([
      ...formattedData.testGroupList,
      ...(formattedData.customTestGroup.length
        ? [
            {
              name: t('CREATE_SCREENING_CUSTOM_TEST_GROUP_TITLE'),
              type: TestGroupTypes.CUSTOM,
              order: data?.customTestGroup?.order,
              weight: data?.customTestGroup?.weight,
              duration: formattedData.customScreeningTGDuration,
              tests: formattedData.customTestGroup,
              hasSkipQuestion: formattedData.customTestGroupHasSkipQuestion,
              screeningSortStrategy: getTestGroupScreeningStrategy(
                data?.customTestGroup?.screeningSortStrategy
              ),
              // Include recordingConfig if AI_INTERVIEW, use valid config or default
              ...(data?.customTestGroup?.screeningSortStrategy === StrategyValues.AI_INTERVIEW
                ? {
                    recordingConfig: data?.customTestGroup?.recordingConfig?.mode
                      ? data?.customTestGroup?.recordingConfig
                      : { mode: RecordingMode.AUDIO_ONLY },
                  }
                : {}),
              id: v4(),
            },
          ]
        : []),
      ...(formattedData.customChallengeTestGroup.length
        ? [
            {
              name: t('CREATE_SCREENING_CHALLENGE_TEST_GROUP_TITLE'),
              type: TestGroupTypes.CHALLENGE_ASYNC_AI,
              order: data?.customChallengeTestGroup?.order,
              weight: data?.customChallengeTestGroup?.weight,
              duration: 1,
              tests: formattedData.customChallengeTestGroup,
              hasSkipQuestion: false,
              withoutTime: true,
              id: v4(),
            },
          ]
        : []),
    ]);
    dispatch(
      setNewScreeningData({
        ...newScreening,
        testGroups: formattedData.testGroupList.map((testGroup) => ({
          ...testGroup,
        })),
        customTestGroupHasSkipQuestion: formattedData.customTestGroupHasSkipQuestion,
        customTestGroup: formattedData.customTestGroup,
        customChallengeTestGroup: formattedData.customChallengeTestGroup,
        customScreeningTGDuration: formattedData.customScreeningTGDuration,
        allTests: formattedAllTests,
        prefilterQuestions: formattedData?.prefilterQuestions,
        prefilterQuestionsDelay: formattedData?.prefilterQuestionsDelay,
        welcomeVideoUrl: formattedData.welcomeVideoUrl,
        welcomeVideoTitle: formattedData.welcomeVideoTitle,
      })
    );
    setPreviousData(formattedData);
    SetPreviousWeightsAndOrders(
      formattedAllTests.map((item) => ({
        [item.id]: {
          weight: item.weight,
          order: item.order,
        },
      }))
    );
    handleCustomTestGroupStrategy(data?.customTestGroup?.screeningSortStrategy);
    // Always set recordingConfig - use from draft or default
    // Check if recordingConfig exists and has a mode property (not just an empty object)
    const hasValidRecordingConfig = data?.customTestGroup?.recordingConfig?.mode;
    const recordingConfigToSet = hasValidRecordingConfig
      ? data.customTestGroup.recordingConfig
      : data?.customTestGroup?.screeningSortStrategy === StrategyValues.AI_INTERVIEW
      ? { mode: RecordingMode.AUDIO_ONLY }
      : null;
    if (recordingConfigToSet) {
      handleRecordingConfig(recordingConfigToSet);
    }
  };

  const handleSetSearchType = async (id, index, role) => {
    setEnableCreateButton(true);
    const modifiedSearchTypes = { ...searchTypes };
    let evaluationSearchTypes = { ...(evaluation.searchTypes || {}) };

    if (isSelected(id, index)) {
      const newSelection = evaluationSearchTypes[index].filter((item) => item !== id);
      evaluationSearchTypes[index] = newSelection.length ? newSelection : null;

      if (evaluationSearchTypes) {
        Object.keys(evaluationSearchTypes).forEach((key) => {
          if (Number(key) > index) {
            delete evaluationSearchTypes[key];
          }
        });
      }

      if (modifiedSearchTypes) {
        Object.keys(modifiedSearchTypes).forEach((key) => {
          if (Number(key) > index) {
            delete modifiedSearchTypes[key];
          }
        });
      }

      setSearchTypes(modifiedSearchTypes);
      deselectTg();
    } else {
      const isAnotherSelected =
        evaluationSearchTypes[index] && Object.keys(evaluationSearchTypes[index])?.length;
      if (isAnotherSelected) {
        if (index > 2) {
          evaluationSearchTypes[index] = [...(evaluationSearchTypes[index] || []), id];
        } else {
          evaluationSearchTypes[index] = [id];
          const newEvaluationSearchTypes = {};
          for (let i = 0; i <= index; i++) {
            newEvaluationSearchTypes[i] = evaluationSearchTypes[i];
          }
          evaluationSearchTypes = newEvaluationSearchTypes;

          // if (Object.keys(searchTypes).length <= 4) {
          for (let i = index + 1; i <= Object.keys(modifiedSearchTypes)?.length; i++) {
            delete modifiedSearchTypes[i];
          }
        }
        deselectTg();
        // }
      } else {
        evaluationSearchTypes[index] = [id];
      }
      const searchType = searchTypes[index].find((item) => item._id === id);
      if (
        Object.keys(searchTypes).length == 1 ||
        searchType?.type !== EVALUATION_SEARCH_TYPES.RECRUITMENT
      ) {
        await fetchSearchTypes(id, index + 1, modifiedSearchTypes);
      } else {
        setSearchTypes(modifiedSearchTypes);
      }
    }

    handleSetEvaluationData({
      ...evaluation,
      roleId: role || evaluation.roleId,
      searchTypes: evaluationSearchTypes,
    });
  };

  let createEvaluationErrors = [];
  const checkEnableCreateButton = () => {
    const searchTypes = hasSearchTypes(evaluation?.searchTypes);

    const errors = {};

    if (!searchTypes) {
      errors.searchTypes = t('CREATE_EVALUATION_SEARCH_TYPES_REQUIRED_TEXT');
    }

    createEvaluationErrors = Object.keys(errors).map((key) => ({ id: key, text: errors[key] }));

    return searchTypes;
  };

  useEffect(() => {
    setPreventReload((existingChanges && !showExitModal) || Boolean(incompleteQuestionValidation));
    if (existingChanges) {
      setEnableCreateButton(true);
    }
  }, [existingChanges, showExitModal, incompleteQuestionValidation, evaluation.searchTypes]);

  useEffect(() => {
    if (queryData.draftId && !evaluation.name.length) {
      setLoadingCreate(true);
      getRecruitSessionDraft(queryData?.draftId).then((res) => {
        setFormattedData(res);
        setLoadingCreate(false);
      });
    }
  }, [queryData?.draftId]);

  const handleClearData = (filters = true) => {
    dispatch(
      setEvaluationData({
        name: '',
        startDate: {
          day: new Date().toISOString(),
          hours: '00:00',
        },
        endDate: {
          day: new Date('9999-01-01').toISOString(),
          hours: '00:00',
        },
        allowCertificateDownload: true,
        proctoringEnabled: true,
        proctoringAIEnabled: false,
        requireCompletedProfile: false,
        reminderToCandidates: true,
        sectionsWithoutOrder: true,
        reviewType: ReviewTypeEnum.GLOBAL,
        emptyDates: true,
        roleId: null,
        searchTypes: [],
      })
    );
    dispatch(
      setNewScreeningData({
        ...newScreening,
        testGroups: [],
        customTestGroup: [],
        prefilterQuestionsDelay: undefined,
        prefilterQuestions: [],
        customScreeningTGDuration: '0 min',
        welcomeVideoUrl: undefined,
        welcomeVideoTitle: undefined,
        allTests: [],
        langCodesAIOptions: [],
        langCodesAIRubrics: {},
      })
    );
    setStep(0);
    if (filters) {
      dispatch(setEvaluationSearchTypes({}));
      handleClearFilters(false, false);
    }
  };

  const handleEvaluationName = (e) => {
    if (evaluation.name.length <= 50 || evaluation.name?.includes(t('COPY_TO'))) {
      setEnableCreateButton(true);
      dispatch(
        setEvaluationData({
          ...evaluation,
          name: e.target.value,
        })
      );
    }
  };

  const handleEvaluationLanguage = (e) => {
    setEnableCreateButton(true);
    dispatch(
      setEvaluationData({
        ...evaluation,
        language: e,
      })
    );
    dispatch(
      setNewScreeningFilters({
        ...newScreeningFilters,
        language: [e],
      })
    );
  };

  const handleEvaluationQuickRole = (id) => {
    if (id) {
      dispatch(
        setEvaluationData({
          ...evaluation,
          quickStartRoleId: id,
        })
      );
    }
  };

  const handleSetEvaluationData = (data) => {
    dispatch(setEvaluationData(data));
  };

  const formatDateWithObject = (day, hour) => {
    const date = moment(day);
    date.set({ hours: 0, minutes: 0, seconds: 0 });
    date.add(hour).utc();
    return date.toDate();
  };

  const hasSearchTypes = (evaluationSearchTypes) => {
    const evaluationFirstStepSearchTypeId = evaluationSearchTypes?.[0]?.[0];
    const DONT_NEED_HELP_ID = searchTypes?.[0]?.find((e) => e.type === 'DONT_NEED_HELP')?.id;
    return (
      evaluationFirstStepSearchTypeId === DONT_NEED_HELP_ID ||
      (evaluationSearchTypes?.[0]?.length > 0 && evaluationSearchTypes?.[1]?.length > 0)
    );
  };
  const handleCreateDraft = async () => {
    setLoadingCreateDraft(true);
    const enabled = checkEnableCreateButton();
    setEnableCreateButton(enabled);
    setPreventReload(false);
    if (enabled) {
      const postData = {
        name: evaluation.name.length ? evaluation.name : t('CREATE_EVALUATION_NO_NAME'),
        startDate: formatDateWithObject(evaluation.startDate.day, 0),
        endDate: formatDateWithObject(evaluation.endDate.day, 0),
        companyId: getLocalStorageWorkspace()?.companyId,
        language: evaluation.language ?? 'es',
        workspaceId: currentWorkspace,
        purpose: null,
        customPurpose: null,
        allowCertificateDownload: evaluation.allowCertificateDownload ?? true,
        proctoringEnabled: evaluation.proctoringEnabled ?? true,
        proctoringAIEnabled: evaluation.proctoringAIEnabled ?? false,
        exclusiveUsers: evaluation.exclusiveUsers ?? false,
        accessWithoutAccount: evaluation.accessWithoutAccount ?? false,
        requireCompletedProfile: evaluation.requireCompletedProfile ?? false,
        reminderToCandidates: evaluation.reminderToCandidates ?? true,
        sectionsWithoutOrder: evaluation.sectionsWithoutOrder ?? true,
        reviewType: evaluation.reviewType ?? ReviewTypeEnum.GLOBAL,
        quickStartRoleId: evaluation.quickStartRoleId,
        roleId: evaluation.roleId,
        customRole: evaluation.customRole,
        emptyDates: true,
        searchTypes: evaluation.searchTypes ?? [],
      };
      if (
        formatDateWithObject(evaluation?.startDate?.day, evaluation?.startDate?.hours) >=
        formatDateWithObject(evaluation?.endDate?.day, evaluation?.endDate?.hours)
      ) {
        notification.open({
          message: t('CREATE_SEARCH_STAGE_END_DATE_BEFORE_START_DATE'),
          type: 'error',
        });
        setLoadingCreateDraft(false);
      } else {
        TrackEventGTM(EventsGTM.CREATE_EVALUATION, {
          event_category: 'Create draft',
          event_action: 'click',
        });
        await postRecruitSessionDraft(postData)
          .then((res) => {
            history.replace({ search: `draftId=${res.id}` });
            setFormattedData(res);

            getRecruitSessionDraft(res.id).then((res) => {
              setFormattedData(res);
              setStep(1);
            });
            TrackEventGTM(EventsGTM.CREATE_EVALUATION, {
              event_category: 'Step 2 create evaluation',
              event_action: 'click',
            });
            notification.open({
              message: t('CREATE_EVALUATION_DRAFT_CREATED_NOTIFICATION'),
              type: 'success',
            });
          })
          .finally(() => {
            setLoadingCreateDraft(false);
          });
      }
    } else {
      notification.open({
        message: createEvaluationErrors.map((error) => error.text).join('\n'),
        type: 'error',
      });
      setLoadingCreateDraft(false);
    }
  };

  const handleEditDraft = async () => {
    setPreventReload(false);
    const customTestGroup = newScreening?.allTests?.find(
      (item) => item.type === TestGroupTypes.CUSTOM
    );
    const tests = newScreening?.allTests?.find(
      (item) => item.type === TestGroupTypes.CHALLENGE_ASYNC_AI
    )?.tests;
    if (tests) {
      await uploadAndUpdateFiles(tests);
    }
    const patchData = {
      name: evaluation.name,
      language: evaluation.language,
      startDate: formatDateWithObject(evaluation.startDate.day, evaluation.startDate.hours),
      endDate: formatDateWithObject(evaluation.endDate.day, evaluation.endDate.hours),
      allowCertificateDownload: evaluation.allowCertificateDownload ?? true,
      proctoringEnabled: evaluation.proctoringEnabled ?? true,
      proctoringAIEnabled: evaluation.proctoringAIEnabled ?? false,
      exclusiveUsers: evaluation.exclusiveUsers ?? false,
      accessWithoutAccount: evaluation.accessWithoutAccount ?? false,
      requireCompletedProfile: evaluation.requireCompletedProfile ?? false,
      reminderToCandidates: evaluation.reminderToCandidates ?? true,
      sectionsWithoutOrder: evaluation.sectionsWithoutOrder ?? true,
      reviewType: evaluation.reviewType ?? ReviewTypeEnum.GLOBAL,
      testGroupList:
        newScreening?.allTests?.filter(
          (item) =>
            item.type !== TestGroupTypes.CUSTOM && item.type !== TestGroupTypes.CHALLENGE_ASYNC_AI
        ) ?? [],
      customTestGroup: validCustomTestGroup()
        ? {
            weight: newScreening?.allTests?.find((item) => item.type === TestGroupTypes.CUSTOM)
              ?.weight,
            order: newScreening?.allTests?.find((item) => item.type === TestGroupTypes.CUSTOM)
              ?.order,
            duration: customScreeningTotalTime,
            tests: newScreening?.customTestGroup,
            hasSkipQuestion: customTestGroup?.hasSkipQuestion ?? true,
            screeningSortStrategy: getTestGroupScreeningStrategy(customTestGroupStrategy),
            ...(customTestGroupStrategy === StrategyValues.AI_INTERVIEW && recordingConfig
              ? { recordingConfig }
              : {}),
            type: TestGroupTypes.CUSTOM,
          }
        : {},
      customChallengeTestGroup: validCustomChallengeTestGroup()
        ? {
            weight: newScreening?.allTests?.find(
              (item) => item.type === TestGroupTypes.CHALLENGE_ASYNC_AI
            )?.weight,
            order: newScreening?.allTests?.find(
              (item) => item.type === TestGroupTypes.CHALLENGE_ASYNC_AI
            )?.order,
            duration: newScreening?.allTests?.find(
              (item) => item.type === TestGroupTypes.CHALLENGE_ASYNC_AI
            )?.duration,
            tests,
            hasSkipQuestion: false,
            withoutTime: true,
            type: TestGroupTypes.CHALLENGE_ASYNC_AI,
          }
        : {},
      purpose: evaluation.purpose,
      customPurpose: evaluation.customPurpose,
      emptyDates: evaluation.emptyDates,
      roleId: evaluation.roleId,
      workspaceId: currentWorkspace,
      ...(newScreening?.prefilterQuestions?.length > 0
        ? {
            prefilterQuestionsDraft: {
              name: evaluation.name,
              workspaceId: currentWorkspace,
              companyId: user?.companyId,
              negativeDelay: newScreening.prefilterQuestionsDelay,
              type: 'PRE_FILTER',
              testGroups: [
                {
                  tests: newScreening.prefilterQuestions,
                  name: evaluation.name,
                  type: 'PRE_FILTER',
                  workspaceId: currentWorkspace,
                  companyId: user?.companyId,
                },
              ],
            },
          }
        : {}),
      welcomeVideoUrl: newScreening.welcomeVideoUrl,
      welcomeVideoTitle: newScreening.welcomeVideoTitle,
      searchTypes: evaluation.searchTypes,
    };

    try {
      const res = await editRecruitSessionDraft(queryData?.draftId, patchData);
      setFormattedData(res);
      notification.open({
        message: t('CREATE_EVALUATION_DRAFT_EDITED_NOTIFICATION'),
        type: 'success',
      });

      if (showExitModal) {
        setShowExitModal(false);
      }
      setStep(1);
    } catch (error) {
      console.error('Error editing draft:', error);
      console.error('Draft data:', patchData);
      console.error('Custom test group:', patchData.customTestGroup);
      throw error;
    }
  };

  const determineNotification = () => {
    if (!evaluation.name) {
      notification.open({
        message: t('CREATE_EVALUATION_NAME_REQUIRED_TEXT'),
        type: 'error',
      });
      return;
    }
    if (!evaluation.startDate.day || !evaluation.endDate.day) {
      notification.open({
        message: t('CREATE_EVALUATION_START_AND_END_DATES_REQUIRED_TEXT'),
        type: 'error',
      });
      return;
    }
    if (!validCustomTestGroup()) {
      if (!parsedCustomTGDuration) {
        notification.open({
          message: t('CREATE_EVALUATION_CUSTOM_TESTGROUP_TIME_REQUIRED_TEXT'),
          type: 'error',
        });
      }
      return;
    }
    if (!newScreening.allTests.length) {
      notification.open({
        message: t('CREATE_EVALUATION_AT_LEAST_ONE_TEST_OR_QUESTION_REQUIRED_TEXT'),
        type: 'error',
      });
      return;
    }
    if (availableWeight < 0) {
      notification.open({
        message: t('CREATE_EVALUATION_EXCEEDED_TESTS_WEIGHT_TEXT'),
        type: 'error',
      });
      return;
    }
    if (availableWeight > 0) {
      notification.open({
        message: t('CREATE_EVALUATION_REMAINING_TESTS_WEIGHT_TEXT'),
        type: 'error',
      });
      return;
    }
    if (missingTestgroupWeight) {
      notification.open({
        message: t('CREATE_EVALUATION_MISSING_SOME_TEST_WEIGHT_TEXT'),
        type: 'error',
      });
    }
  };

  const handleOpenEvaluation = async () => {
    // Check if all testgroups with preSettings have a selectedPresetId
    const testGroupsWithoutPreset = getTestGroupsMissingPreset(newScreening?.allTests);

    if (testGroupsWithoutPreset.length > 0) {
      const testGroupNames = testGroupsWithoutPreset
        .map((tg) => getTranslatedValue(tg.name, evaluation.language ?? key))
        .join(', ');
      notification.open({
        message: t('CREATE_EVALUATION_MISSING_PRESET_CONFIG'),
        description: t('CREATE_EVALUATION_MISSING_PRESET_CONFIG_DESCRIPTION').replace(
          '{{testGroups}}',
          testGroupNames
        ),
        type: 'error',
      });
      setLoadingCreateEvaluation(false);
      return;
    }

    setLoadingCreateEvaluation(true);

    const customTestGroup = newScreening?.allTests?.find(
      (item) => item.type === TestGroupTypes.CUSTOM
    );
    const customChallengeTestGroup = newScreening?.allTests?.find(
      (item) => item.type === TestGroupTypes.CHALLENGE_ASYNC_AI
    );
    function getFormattedCustomTestGroup() {
      const item = formatCustomTestGroupToPost(customTestGroup);
      if (!item) return item;

      const hasCodeAITests = item?.tests.some((test) => test.answerType === AnswerTypeEnum.CODE_AI);

      const hasValidLangCodesAI =
        newScreening?.langCodesAIOptions?.length > 0 &&
        Object.keys(newScreening?.langCodesAIRubrics || {}).length > 0;

      const result = { ...item };
      // Add recordingConfig if AI_INTERVIEW strategy is selected
      // Check both the customTestGroup and the formatted item for the strategy
      const isAIInterview =
        item?.screeningSortStrategy === StrategyValues.AI_INTERVIEW ||
        customTestGroup?.screeningSortStrategy === StrategyValues.AI_INTERVIEW ||
        customTestGroupStrategy === StrategyValues.AI_INTERVIEW;

      if (isAIInterview) {
        // Always include recordingConfig if AI_INTERVIEW, with default if empty
        // Try to get recordingConfig from: 1) customTestGroup object, 2) Redux state, 3) default
        const finalRecordingConfig = customTestGroup?.recordingConfig?.mode
          ? customTestGroup.recordingConfig
          : recordingConfig?.mode
          ? recordingConfig
          : { mode: RecordingMode.AUDIO_ONLY };

        result.recordingConfig = finalRecordingConfig;
      }

      if (hasCodeAITests && hasValidLangCodesAI) {
        result.withCustomLangCodesAI = true;
      }

      return result;
    }

    const formattedCustomTestGroup = getFormattedCustomTestGroup();
    if (customChallengeTestGroup && !customChallengeTestGroup?.tests?.[0]?.extension) {
      await uploadAndUpdateFiles(customChallengeTestGroup.tests);
    }
    const formattedCustomChallengeTestGroup =
      formatChallengeTestGroupToPost(customChallengeTestGroup);
    const alkemyTestGroups =
      newScreening?.allTests?.filter(
        (item) =>
          item.type !== TestGroupTypes.CUSTOM && item.type !== TestGroupTypes.CHALLENGE_ASYNC_AI
      ) ?? [];
    const formattedTestGroups = formatOrder([
      ...alkemyTestGroups,
      formattedCustomTestGroup,
      formattedCustomChallengeTestGroup,
    ]).map((item) => ({
      ...item,
      weight: item.weight ?? 0,
      tests: item.tests.map((test) => ({
        ...test,
        companyId: getLocalStorageWorkspace()?.companyId,
      })),
      ...(item?.withCustomLangCodesAI &&
      newScreening?.langCodesAIOptions?.length > 0 &&
      Object.keys(newScreening?.langCodesAIRubrics || {}).length > 0
        ? {
            rubricsAI: newScreening?.langCodesAIRubrics,
            langCodesAvailable:
              item?.preLangCodesAvailable?.length > 0
                ? (() => {
                    const filtered = item.preLangCodesAvailable.filter((code) =>
                      newScreening?.langCodesAIOptions?.includes(code)
                    );
                    return filtered.length > 0 ? filtered : item.preLangCodesAvailable;
                  })()
                : newScreening?.langCodesAIOptions,
          }
        : {}),
    }));
    const dataToPost = {
      id: queryData.draftId,
      name: evaluation.name,
      language: evaluation.language ?? 'es', // handle case for old draft without language
      companyId: getLocalStorageWorkspace()?.companyId,
      userId: user?.id,
      duration: customScreeningTotalTime + testsDuration,
      startDate: formatDateWithObject(evaluation.startDate.day, evaluation.startDate.hours),
      endDate: formatDateWithObject(evaluation.endDate.day, evaluation.endDate.hours),
      testGroups: formattedTestGroups,
      workspaceId: currentWorkspace,
      purpose: evaluation.purpose ?? 'OTHER',
      customPurpose: evaluation.customPurpose,
      allowCertificateDownload: evaluation.allowCertificateDownload ?? true,
      proctoringEnabled: evaluation.proctoringEnabled ?? true,
      proctoringAIEnabled: evaluation.proctoringAIEnabled ?? false,
      requireCompletedProfile: evaluation.requireCompletedProfile ?? false,
      reminderToCandidates: evaluation.reminderToCandidates ?? true,
      sectionsWithoutOrder: evaluation.sectionsWithoutOrder ?? true,
      reviewType: evaluation.reviewType ?? ReviewTypeEnum.GLOBAL,
      emptyDates: evaluation.emptyDates,
      exclusiveUsers: evaluation.exclusiveUsers ?? false,
      accessWithoutAccount: evaluation.accessWithoutAccount ?? false,
      prefilterQuestionsDraft: evaluation?.prefilterQuestions,
      welcomeVideoUrl: newScreening.welcomeVideoUrl,
      welcomeVideoTitle: newScreening.welcomeVideoTitle,
      availableWeight,
    };
    if (disableOpenEvaluation) {
      determineNotification();
    } else {
      TrackEventGTM(EventsGTM.CREATE_EVALUATION, {
        event_category: 'Open Evaluation',
        event_action: 'click',
      });
      setPreventReload(false);
      try {
        const res = await openEvaluation(dataToPost);
        if (Object.keys(res)) {
          notification.open({
            message: t('SEARCH_CREATED_SUCCESSFULLY'),
            type: 'success',
          });
          dispatch(
            setNewSearchData({
              ...newSearch,
              name: res.name,
              step: newSearch.step + 1,
              stages: res.stages,
            })
          );
          dispatch(setSearchInvitationsData({}));
          dispatch(setSendEmailFrom('new'));
          history.push('/createsearch/success');
        } else {
          notification.open({
            message: t('SEARCH_CREATE_ERROR'),
            type: 'error',
          });
        }
      } catch (e) {
        console.log(e);
        notification.open({
          message: t('SEARCH_CREATE_ERROR'),
          type: 'error',
        });
      } finally {
        setLoadingCreate(false);
        setLoadingCreateEvaluation(false);
      }
    }
  };

  const fetchTests = async ({ rolesIds, favorites, persistFilters = false, testgroupsIds }) => {
    const isEvaluationPage = history.location.pathname.includes(CREATE_EVALUATION);
    setLoadingTests(true);
    const formattedFilters = formatFilters(newScreeningFilters);
    await getScreeningTestGroups(
      persistFilters
        ? {
            ...formattedFilters,
            rolesIds,
            favorites,
            testgroupsIds,
            ...(isEvaluationPage ? { recommendTestGroups: true } : {}),
          }
        : {
            rolesIds,
            favorites,
            testgroupsIds,
            lang: formattedFilters?.lang,
            ...(isEvaluationPage ? { recommendTestGroups: true } : {}),
          }
    ).then((res) => {
      dispatch(setTestGroupsData(res));
      setLoadingTests(false);
    });
  };

  const handleChangeStep = async (nextStep) => {
    if (nextStep > 3) return;
    if (step === 0) {
      const enabled = checkEnableCreateButton();
      if (!enabled) {
        notification.open({
          message: createEvaluationErrors.map((error) => error.text).join('\n'),
          type: 'error',
        });

        return;
      }
    }
    if (step === 1 && nextStep > step) {
      if (incorrectTestgroupLanguages) {
        notification.open({
          message: t('EDIT_DRAFT_LANGUAGE_ERROR'),
          type: 'error',
        });
        return;
      }
    }
    if (step === 2) {
      if (incompleteQuestionValidation && newScreening.customTestGroup.length > 0) {
        notification.open({
          message: t('CREATE_CUSTOM_TEST_INCOMPLETE_TEST_FORM_ERROR'),
          type: 'error',
        });
        dispatch(setCustomTestGroupIncompleteQuestion(true));
        return;
      }
      if (incompletePrefilterQuestionValidation) {
        notification.open({
          message: t('CREATE_CUSTOM_PREFILTER_INCOMPLETE_FORM_ERROR'),
          type: 'error',
        });
        return;
      }
      if (!validCustomTestGroup()) {
        determineNotification();
        return;
      }
    }
    if (evaluation.name?.length > 50) {
      notification.open({
        message: t('CREATE_EVALUATION_NAME_TOO_LONG'),
        type: 'error',
        description: t('CREATE_SCREENING_MAX_CHAR_NAME_50'),
      });
      return;
    }
    if (existingChanges) {
      try {
        await handleEditDraft();
      } catch (e) {
        notification.open({
          message: t('ERROR_ON_CREATE_DRAFT'),
          type: 'error',
        });
        return;
      }
    }
    setStep(nextStep);
  };

  const saveChangesModal = () => (
    <Modal
      closeBtnClassname="font-weight-bold text-muted"
      show={showExitModal}
      minWidth={false}
      onClose={() => {
        setShowExitModal(false);
      }}
      setShow={(value) => {
        setShowExitModal(value);
      }}
      className="create-evaluation-custom-modal"
    >
      <div className="mt-5 d-flex flex-column justify-content-center align-items-center pt-1 pb-4">
        <CreateEvaluationModalImage />
        <span className=" text-base font-weight-600 py-3">
          {t('CREATE_EVALUATION_SAVE_CHANGES_MODAL_TEXT')}
        </span>
        <div className="w-75 d-flex justify-content-between align-items-center">
          <button
            type="button"
            className="border-0 bg-transparent text-blue-principal text-xs font-weight-bold "
            onClick={() => {
              if (showExitModal) {
                setShowExitModal(false);
                history.push(SCREENING_SEARCHES);
              }
            }}
          >
            {t('CREATE_EVALUATION_SAVE_CHANGES_DONT_SAVE_BUTTON')}
          </button>
          <button
            onClick={async () => {
              if (queryData?.draftId) {
                if (
                  formatDateWithObject(evaluation?.startDate?.day, evaluation?.startDate?.hours) >=
                  formatDateWithObject(evaluation?.endDate?.day, evaluation?.endDate?.hours)
                ) {
                  notification.open({
                    message: t('CREATE_SEARCH_STAGE_END_DATE_BEFORE_START_DATE'),
                    type: 'error',
                  });
                  setShowExitModal(false);
                  return;
                }
                if (!validCustomTestGroup()) {
                  determineNotification();
                  setShowExitModal(false);
                  return;
                }
                if (incompleteQuestionValidation) {
                  setShowExitModal(false);
                  notification.open({
                    message: t('CREATE_CUSTOM_TEST_INCOMPLETE_TEST_FORM_ERROR'),
                    type: 'error',
                  });
                  dispatch(setCustomTestGroupIncompleteQuestion(true));
                  return;
                }
                if (incompletePrefilterQuestionValidation) {
                  notification.open({
                    message: t('CREATE_CUSTOM_PREFILTER_INCOMPLETE_FORM_ERROR'),
                    type: 'error',
                  });
                  return;
                }
                await handleEditDraft();
              } else {
                if (!evaluation.name) {
                  determineNotification();
                  setShowExitModal(false);
                  return;
                }
                await handleCreateDraft();
              }
              if (showExitModal) {
                setShowExitModal(false);
                history.push(SCREENING_SEARCHES);
              }
            }}
            className="align-self-end px-3 py-2 text-xs text-white font-weight-bold border-0 outline-none bg-maya-blue"
            type="button"
          >
            {t('CREATE_EVALUATION_SAVE_CHANGES_SAVE_BUTTON')}
          </button>
        </div>
      </div>
    </Modal>
  );

  const deleteLastProperty = (obj) => {
    if (!obj || typeof obj !== 'object') return {};

    const newObj = { ...obj };
    const keys = Object.keys(newObj);
    if (keys.length === 0) return {};

    const lastKey = keys.reduce((maxKey, currentKey) => {
      return Number(currentKey) > Number(maxKey) ? currentKey : maxKey;
    }, keys[0]);

    delete newObj[lastKey];
    return newObj;
  };
  const fetchSearchTypes = async (id, index, modifiedSearchTypes) => {
    await getSearchTypes(id).then((res) => {
      if (res.length) {
        const formattedSearchTypes = {
          ...(modifiedSearchTypes ?? searchTypes),
          [index]: res,
        };
        setSearchTypes(formattedSearchTypes);
      }
    });
  };

  const setSearchTypesData = async (evaluationSearchTypes) => {
    const keys = Object.keys(evaluationSearchTypes);
    let searchTypesToPost = {};
    const firstSearchTypes = await getSearchTypes('');
    searchTypesToPost = {
      0: firstSearchTypes,
    };
    for (const key of keys) {
      const numKey = Number(key);
      const idToGetNextSearchTypes = evaluationSearchTypes[key]?.[0];
      if (!idToGetNextSearchTypes) break;
      const searchTypes = await getSearchTypes(idToGetNextSearchTypes);
      if (searchTypes.length) {
        searchTypesToPost = {
          ...searchTypesToPost,
          [numKey + 1]: searchTypes,
        };
      }
    }

    setSearchTypes(searchTypesToPost);
  };

  const setSearchTypes = (data) => {
    dispatch(setEvaluationSearchTypes(data));
  };

  const [preSelectionLoading, setPreSelectionLoading] = useState(true);
  const [preSelectedTestgroups, setPreselectedTestgroups] = useState([]);
  const [testgroupsToShowUnselected, setTestgroupsToShowUnselected] = useState([]);

  const setTestgroupsData = async () => {
    const searchTypesInEvaluationLength = Object.keys(evaluation?.searchTypes ?? {})?.length;
    const searchTypesLength = Object.keys(searchTypes ?? {})?.length;
    setPreSelectionLoading(true);
    if (searchTypesInEvaluationLength >= 2 && searchTypesLength >= 2) {
      const lastIndexSearchTypeIds = evaluation.searchTypes[searchTypesLength - 1];
      const penultimateIndexSearchTypeIds =
        searchTypesLength >= 3 ? evaluation.searchTypes[searchTypesLength - 2] : [];

      const searchTypesSelectedData = [
        ...(searchTypes[searchTypesLength - 1]?.filter((item) =>
          lastIndexSearchTypeIds?.includes(item._id)
        ) ?? []),
        ...(searchTypes[searchTypesLength - 2]?.filter((item) =>
          penultimateIndexSearchTypeIds?.includes(item._id)
        ) ?? []),
      ];

      let testgroupsFromRoles = [];
      if (evaluation.roleId) {
        // Solo buscamos testgroups por roleId en el caso del rol detallado, sino traer solo los que estan en la prop testgroupsIds del searchType
        const role = testGroupRolesFilterOptions.find((role) => role.id === evaluation.roleId);
        const roleIds = [role.id];
        if (
          searchTypesSelectedData?.some(
            (searchtype) => searchtype.type === EVALUATION_SEARCH_TYPES.RECRUITMENT
          )
        ) {
          // Lógica en el back de traer los testgroups asignados al rol detallado y secundario en el caso que haya 1 o ninguno.
          await getAutoSelectTestgroups({
            rolesIds: roleIds,
            lang: `${key}`,
            limit: 5,
          }).then((res) => {
            testgroupsFromRoles = res;
          });
        }
      }

      const testgroupIdsToShowSelected = searchTypesSelectedData
        .map((item) => item.testgroupsIds)
        .flat();
      const testgroupsIdsToShowUnselected = searchTypesSelectedData
        .map((item) => item.unselectedTestgroupsIds)
        .flat();

      let testgroupsToPreselectFromFilters = [];
      if (testgroupIdsToShowSelected.length) {
        testgroupsToPreselectFromFilters = await getRecommendedTestgroups(
          testgroupIdsToShowSelected.join(',')
        );
        testgroupsToPreselectFromFilters = testgroupsToPreselectFromFilters.filter((tg) =>
          tg.languages?.includes(evaluation.language)
        );
      }

      const filteredTestgroupsFromRoles = (testgroupsFromRoles ?? []).filter((tg) =>
        tg.languages?.includes(evaluation.language)
      );

      const allTestgroupsToPreselect = [
        ...(testgroupsToPreselectFromFilters ?? []),
        ...filteredTestgroupsFromRoles,
      ];

      setPreselectedTestgroups([...allTestgroupsToPreselect]);

      const allTestgroupsToPreselectIds = allTestgroupsToPreselect.map((tg) => tg.id);

      const updatedTestGroups = newScreening.testGroups.filter(
        (tg) => !allTestgroupsToPreselectIds.includes(tg.id)
      );

      const mappedNewTestgroups = allTestgroupsToPreselect.map((tg) => ({
        ...tg,
        isSelected: true,
      }));
      const updatedAllTests = formatOrder([
        ...newScreening.allTests.filter((tg) => !allTestgroupsToPreselectIds.includes(tg.id)),
        ...mappedNewTestgroups,
      ]);
      dispatch(
        setNewScreeningData({
          ...newScreening,
          testGroups: [...updatedTestGroups, ...mappedNewTestgroups],
          allTests: updatedAllTests,
        })
      );

      if (testgroupsIdsToShowUnselected.length) {
        let testgroups = await getRecommendedTestgroups(testgroupsIdsToShowUnselected.join(','));
        testgroups = testgroups.filter((tg) => tg.languages?.includes(evaluation.language));
        setTestgroupsToShowUnselected(testgroups);
      }

      let categoriesIds = [];
      for (const st of searchTypesSelectedData) {
        if (st.categoriesIds?.length) {
          categoriesIds = [...categoriesIds, ...st.categoriesIds];
        }
      }
      if (categoriesIds.length) {
        handleSelectFilterOptionMultiple(categoriesIds, 'category', false);
      } else {
        handleClearFilters(false, false);
      }

      setPreSelectionLoading(false);
      if (allTestgroupsToPreselect.length === 0) {
        // set time out to match the filtering effects
        setTimeout(() => {
          notification.warning({
            message: t('CREATE_EVALUATION_NO_RECOMMENDED_TESTS'),
            type: 'warning',
          });
        }, 2500);
      }
    } else {
      setPreSelectionLoading(false);
    }
  };

  const handleBackButton = async () => {
    let evaluationSearchTypesModified = deleteLastProperty(evaluation.searchTypes);

    if (
      evaluationSearchTypesModified &&
      Object.keys(evaluationSearchTypesModified).length === 1 &&
      evaluationSearchTypesModified.hasOwnProperty('0')
    ) {
      evaluationSearchTypesModified = {};
      await fetchSearchTypes('', 0, {});
    } else if (Object.keys(searchTypes).length > 1) {
      setSearchTypes(deleteLastProperty(searchTypes));
    }

    const updatedEvaluation = {
      ...evaluation,
      searchTypes: evaluationSearchTypesModified,
      roleId: null,
    };

    if (newScreening.allTests?.length) {
      deselectTg();
      handleClearFilters();
    }

    handleSetEvaluationData(updatedEvaluation);
  };

  const isSelected = (id, index) => evaluation?.searchTypes?.[index]?.includes(id);

  const clearDataOnSelectChange = () => {
    setPreselectedTestgroups([]);
    setTestgroupsToShowUnselected([]);
    deselectTg();
  };

  return {
    step,
    draftId: queryData?.draftId,
    showExitModal,
    enableCreateButton,
    setEnableCreateButton,
    existingChanges,
    evaluation,
    loadingCreate,
    loadingTests,
    disableEditButton,
    disableOpenEvaluation,
    disableCustomTestGroupWeight,
    missingTestgroupWeight,
    preventReload,
    incompleteQuestionValidation,
    EvaluationPurposesOptions,
    fetchTests,
    saveChangesModal,
    setStep,
    setLoadingTests,
    setShowExitModal,
    handleEvaluationName,
    handleEvaluationLanguage,
    handleEvaluationQuickRole,
    handleClearData,
    handleSetEvaluationData,
    handleCreateDraft,
    handleOpenEvaluation,
    handleEditDraft,
    handleChangeStep,
    loadingCreateDraft,
    loadingCreateEvaluation,
    fetchSearchTypes,
    searchTypes,
    setSearchTypes,
    setSearchTypesData,
    setTestgroupsData,
    deleteLastProperty,
    handleBackButton,
    isSelected,
    preSelectionLoading,
    preSelectedTestgroups,
    testgroupsToShowUnselected,
    handleSetSearchType,
    clearDataOnSelectChange,
  };
};

export default useCreateEvaluation;
